import { FC, useEffect, useMemo } from 'react';
import { usePostHog } from '@posthog/react';
import { useTranslation } from 'react-i18next';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import ChevronRightIcon from '@/components/ui/icons/ChevronRightIcon.tsx';
import WhiteBlock from '@/components/ui/WhiteBlock/WhiteBlock.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import StarListElement from '@/components/ui/StarListElement/StarListElement.tsx';
import Separator from '@/components/ui/Separator/Separator.tsx';
import SubscriptionPlanBlock from '@/components/functional/SubscriptionPlanBlock/SubscriptionPlanBlock.tsx';
import Radio from '@/components/functional/RadioInput/Radio.tsx';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { subscriptionSelectSchema } from '$/requests/SubscriptionSelectRequest.ts';
import type { SubscriptionSelectRequest } from '$/requests/SubscriptionSelectRequest.ts';
import useProducts from '@/api/useProducts.ts';
import ProductType from '$/_const/ProductType.ts';
import { Alert, Button, Skeleton } from 'react-daisyui';
import { loadStripe } from '@stripe/stripe-js';
import useCreateCheckoutSession from '@/api/useCreateCheckoutSession.ts';
import SearchParams from '@/_constants/SearchParams.ts';
import { Link, useSearchParams } from 'react-router-dom';
import Routes from '@/router/Routes.ts';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const SubscriptionSelectPage: FC = () => {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const redirectUrlParam = searchParams.get(SearchParams.REDIRECT_URL);
  const { control, handleSubmit, setValue } = useForm<SubscriptionSelectRequest>({
    resolver: zodResolver(subscriptionSelectSchema),
    defaultValues: {
      price_id: undefined,
    },
  });

  const {
    data: products,
    isLoading: isLoadingProducts,
    error: errorLoadingProducts,
  } = useProducts(true, {
    type: {
      comparator: 'in',
      value: [ProductType.SUBSCRIPTION],
    },
  });

  const {
    mutateAsync: createSession,
    isPending: isLoadingCreateSession,
    isError: isErrorCreateSession,
    error: errorCreateSession,
  } = useCreateCheckoutSession();

  const redirectUrl = redirectUrlParam ? decodeURIComponent(redirectUrlParam) : window.location.href;

  const yearSubscription = useMemo(
    () => products?.find((productToFind) => productToFind.interval_count === 12 && productToFind.interval === 'month'),
    [products]
  );

  const monthSubscription = useMemo(
    () => products?.find((productToFind) => productToFind.interval_count === 1 && productToFind.interval === 'month'),
    [products]
  );

  // If the user has a yearly subscription, preselect it as it's the best value
  useEffect(() => {
    if (yearSubscription) {
      setValue('price_id', yearSubscription.price_id);
    }
  }, [setValue, yearSubscription]);

  const handleSubscribe = (data: SubscriptionSelectRequest) => {
    void (async () => {
      try {
        if (isLoadingProducts || isLoadingCreateSession) return;

        const selectedProduct = products?.find((p) => p.price_id === data.price_id);
        posthog?.capture('subscription_checkout_initiated', {
          price_id: data.price_id,
          interval: selectedProduct?.interval,
          interval_count: selectedProduct?.interval_count,
          currency: selectedProduct?.currency,
        });

        const response = await createSession({
          redirectUrl,
          priceId: data.price_id,
        });

        const stripe = await stripePromise;

        return await stripe?.redirectToCheckout({
          sessionId: response.data.sessionId,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <div className="w-[416px] md:h-1 md:grow overflow-auto max-w-full flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <span className="font-feature text-3.5xl font-bold italic">{t('subscriptionSelectPage.header')}</span>
          <span className="text-sm text-text-secondary whitespace-pre-line">
            {t('subscriptionSelectPage.subheader')}
          </span>
          <span className="text-sm text-primary italic">{t('subscriptionSelectPage.tagline')}</span>
        </div>
        {errorLoadingProducts ? <Alert status="error">{errorLoadingProducts.message}</Alert> : null}
        {isErrorCreateSession && <Alert status="error">{errorCreateSession.message}</Alert>}
        <Separator />
        <div className="flex flex-col gap-4">
          <Controller
            control={control}
            name="price_id"
            render={({ field: { onChange, value }, formState: { errors } }) =>
              isLoadingProducts ? (
                <>
                  <Skeleton className="w-full h-24 rounded-xl" />
                  <Skeleton className="w-full h-24 rounded-xl" />
                </>
              ) : !yearSubscription && !monthSubscription ? (
                <p className="text-sm italic text-text-secondary text-center">{t('label.no_products_found')}</p>
              ) : (
                <Radio.Group value={value} onChange={onChange} error={errors.price_id?.message}>
                  {yearSubscription ? (
                    <Radio.Input
                      key={yearSubscription.id}
                      value={yearSubscription.price_id}
                      render={({ value, onChange, error, checked }) => {
                        const yearPricePerMonth = yearSubscription.price / 12;
                        const savePercentage = monthSubscription
                          ? 100 - Math.round((yearSubscription.price / (monthSubscription.price * 12)) * 100)
                          : undefined;

                        return (
                          <SubscriptionPlanBlock
                            currency={yearSubscription.currency}
                            perMonth={yearPricePerMonth}
                            perYear={yearSubscription.price}
                            save={savePercentage}
                            value={value}
                            error={!!error}
                            checked={checked}
                            onChange={onChange}
                          />
                        );
                      }}
                    />
                  ) : null}
                  {monthSubscription ? (
                    <Radio.Input
                      key={monthSubscription.id}
                      value={monthSubscription.price_id}
                      render={({ value, onChange, error, checked }) => {
                        return (
                          <SubscriptionPlanBlock
                            currency={monthSubscription.currency}
                            perMonth={monthSubscription.price}
                            value={value}
                            error={!!error}
                            checked={checked}
                            onChange={onChange}
                          />
                        );
                      }}
                    />
                  ) : null}
                </Radio.Group>
              )
            }
          />
        </div>
        <Separator />
        <WhiteBlock.Wrapper>
          <StarListElement>{t('subscriptionSelectPage.feature1')}</StarListElement>
          <StarListElement>{t('subscriptionSelectPage.feature2')}</StarListElement>
          <StarListElement>{t('subscriptionSelectPage.feature3')}</StarListElement>
        </WhiteBlock.Wrapper>
        <Separator />
        <form className="flex flex-col" onSubmit={handleSubmit(handleSubscribe)}>
          <div className="flex flex-col gap-3">
            <LoadingButton
              disabled={isLoadingProducts || isLoadingCreateSession}
              endIcon={<ChevronRightIcon className="w-3 h-3" />}
              type="submit"
              color="primary"
            >
              {t('button.subscribe_now')}
            </LoadingButton>
            <p className="text-text-secondary text-sm text-center">{t('subscriptionSelectPage.cancelNote')}</p>
          </div>
          <Link to={Routes.DASHBOARD} className="flex flex-col">
            <Button type="button" color="primary" variant="link" className="no-underline">
              {t('button.maybe_later')}
            </Button>
          </Link>
        </form>
      </div>
    </BrandCardSideContent>
  );
};

export default SubscriptionSelectPage;
